// SPDX-FileCopyrightText: 2026 TRUMPF Laser SE
//
// SPDX-License-Identifier: LicenseRef-TRUMPF
import { CreateProjectAckMessage } from '@tls/sw91-communication/types/com.api_db_access';
import { AcknowledgeStatus } from '@tls/sw91-communication/types/com.base';
import { IdToColor } from 'components/common/ColorOptions';
import useProjectDetailsNavigation from 'hooks/useProjectDetailsNavigation';
import useProjectSelectionApi from 'hooks/useProjectSelectionApi';
import { useCallback } from 'react';
import { AppRoutes } from 'utilities/router/AppRoutes';
import type { RequestSettings } from './createProjectDialogHelpers';

type SubmitResult = {
  status: number;
  errorKey: string;
  successKey: string;
};

export default function useSubmitProject(id?: string, duplicateSourceId?: string) {
  const { loading, save, edit, duplicate } = useProjectSelectionApi();
  const { navigateToDetails } = useProjectDetailsNavigation();

  const submitProject = useCallback(
    async (requestSettings: RequestSettings): Promise<SubmitResult> => {
      const { projectName, labelsInfo, tags, strategy, useCase } = requestSettings.settings;
      const request = {
        name: projectName,
        labels: labelsInfo.map((label, index) => ({
          classIndex: index,
          className: label.labelName,
          labelColor: IdToColor(label.labelColor.id),
          labelCategory: label.labelCategory,
          labelGrayScale: 0,
        })),
        tags,
        strategy,
        useCase,
        cameraCalibration: requestSettings.cameraCalibration,
      };

      if (!id && !duplicateSourceId) {
        const result = await save(request);
        const response = CreateProjectAckMessage.fromJSON(result.data);
        const projectId = response.messageHeader?.projectId;

        if (projectId && response.status?.status === AcknowledgeStatus.SUCCESS) {
          navigateToDetails(AppRoutes.ProjectLabelFullPath(projectId), projectId);
        }

        return {
          status: result.status,
          errorKey: 'project.settings.add.error',
          successKey: 'project.settings.add.success',
        };
      }

      if (duplicateSourceId) {
        const result = await duplicate({ ...request, id: duplicateSourceId });
        return {
          status: result.status,
          errorKey: 'project.settings.duplicate.error',
          successKey: 'project.settings.duplicate.success',
        };
      }

      if (id) {
        const result = await edit({ ...request, id });
        return {
          status: result.status,
          errorKey: 'project.settings.edit.error',
          successKey: 'project.settings.edit.success',
        };
      }

      return {
        status: 404,
        errorKey: 'project.settings.add.error',
        successKey: 'project.settings.add.success',
      };
    },
    [duplicate, duplicateSourceId, edit, id, navigateToDetails, save]
  );

  return { loading, submitProject };
}
